import { Trans, useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { useToaster } from "@hooks/useToaster";
import {
  getCardTypes,
  getDisabilityTypes,
  getWheelchairTypes,
  Passenger,
  searchJourney,
  SearchRequest,
} from "@services/travel";
import { ConnectError } from "@connectrpc/connect";
import { DAPNumber } from "../DAPNumber";
import PassengerForm from "./PassengerForm";
import { AdditionalPassengerForm } from "./AdditionalPassengerForm";
import TravelForm from "./TravelForm";
import { Button, Icon, Modal } from "@thetreep/ui";
import { useDAP } from "../dap.hook";
import { useLoadingEffect } from "@hooks/useLoadingEffect";
import { DAP, DAPStatus } from "@gen/dap/v1/dap_pb";
import { useState, useEffect, useRef } from "react";
import { SearchLoader } from "@pages/booking/search/SearchLoader";
import { useLocation, useNavigate } from "react-router";
import { routes } from "@routes";
import {
  Gender,
  PlaneCabinClass,
  TrainCabinClass,
} from "@gen/travel/v1/common_pb";
import { useBookingStore } from "@stores/useBookingStore";
import {
  CardType,
  DisabilityType,
  WheelchairType,
} from "@gen/travel/v1/catalog_pb";
import {
  TravelOverlappingError,
  TravelOverlappingErrorSchema,
  TravelSameMonthError,
  TravelSameMonthErrorSchema,
} from "@gen/travel/v1/search_pb";
import { usePassengerCompanionLogic } from "./usePassengerCompanionLogic";
import { capitalizeFirstLetter } from "@utils/string";
export type CabinClass = TrainCabinClass | PlaneCabinClass;

export function Search() {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { setOutwardResults, setSearchRequest, searchRequest } =
    useBookingStore();

  const { control, getValues, trigger, reset, setValue } =
    useForm<SearchRequest>({
      mode: "onChange",
      defaultValues: searchRequest || {},
    });

  // If we import this hook in components down the tree, the pqssengers might not be synced with this one, that's why we pass everything down instead
  const {
    fields: passengers,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: "additionalPassengers",
  });

  const mainPassengerForm = useForm<Passenger>({
    mode: "onTouched",
    defaultValues: searchRequest?.mainPassenger || { gender: Gender.MALE },
  });

  const { dap, isLoading: isLoadingDAP, errorDapMessage } = useDAP();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useLoadingEffect(isLoading);

  // form data
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);
  const [wheelchairTypes, setWheelchairTypes] = useState<WheelchairType[]>([]);
  const [disabilityTypes, setDisabilityTypes] = useState<DisabilityType[]>([]);

  const [error, setError] = useState<
    TravelOverlappingError | TravelSameMonthError | undefined
  >();

  const travelFormRef = useRef<{ scrollToButton: () => void }>(null);
  const passengerFormRef = useRef<{ scrollToFormError: () => void }>(null);

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = "hidden";
      window.scrollTo(0, 0);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isLoading]);

  useEffect(() => {
    (async () => {
      if (searchRequest?.dapNumber !== dap?.number) {
        reset({
          additionalPassengers: [],
        });
        mainPassengerForm.reset({ gender: Gender.MALE });
      } else {
        reset(searchRequest || {});
        const mainPassengerToRestore = searchRequest?.mainPassenger
          ? {
              ...searchRequest.mainPassenger,
              gender: searchRequest.mainPassenger.gender || Gender.MALE,
            }
          : { gender: Gender.MALE };
        mainPassengerForm.reset(mainPassengerToRestore);
      }
      if (!dap || !canBook(dap)) return;
      try {
        const [cardTypes, wheelchairTypes, disabilityTypes] = await Promise.all(
          [
            getCardTypes(dap.number),
            getWheelchairTypes(dap.number),
            getDisabilityTypes(dap.number),
          ],
        );
        setCardTypes(cardTypes.cards);
        setWheelchairTypes(wheelchairTypes.wheelchairs);
        setDisabilityTypes(disabilityTypes.disabilities);
      } catch (e) {
        if (e instanceof ConnectError && e.rawMessage) {
          addToast("error", e.rawMessage);
        } else {
          addToast("error", t("genericError"));
        }
      }
    })();
  }, [dap]);

  async function onSubmit(bypass: boolean = false) {
    if (!dap) {
      return;
    }

    // check that every form is valid
    const valid = await Promise.all([mainPassengerForm.trigger(), trigger()]);
    if (!valid.every((v) => v)) {
      // scroll to the first error
      const firstErrorField = document.querySelector('[aria-invalid="true"]');
      if (firstErrorField) {
        passengerFormRef.current?.scrollToFormError();
      } else {
        travelFormRef.current?.scrollToButton();
      }

      return;
    }

    const form = getValues();
    const passenger = {
      ...mainPassengerForm.getValues(),
      firstName: dap.firstName!,
      lastName: dap.lastName!,
    };
    try {
      setIsLoading(true);
      const request = {
        ...form,
        dapNumber: dap?.number,
        mainPassenger: passenger,
        bypassOverlapping: bypass,
      };
      const response = await searchJourney(request);
      setSearchRequest(request);
      setOutwardResults(response);
      navigate(routes.RESULTS + search, {
        state: { searchRequest: request, searchResponse: response },
      });
    } catch (e) {
      if (e instanceof ConnectError) {
        if (e.findDetails(TravelOverlappingErrorSchema).length > 0) {
          setError(e.findDetails(TravelOverlappingErrorSchema)[0]);
        } else if (e.findDetails(TravelSameMonthErrorSchema).length > 0) {
          setError(e.findDetails(TravelSameMonthErrorSchema)[0]);
        } else if (e.rawMessage) {
          addToast("error", capitalizeFirstLetter(e.rawMessage));
        } else {
          addToast("error", t("genericError"));
        }
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsLoading(false);
    }
  }

  function canBook(dap: DAP): boolean {
    return (
      !!dap &&
      dap.status == DAPStatus.DAP_STATUS_VALID &&
      dap.isBookableByCaller &&
      !!dap.maxJourneys &&
      dap.journeysBooked < dap.maxJourneys
    );
  }

  const {
    canBeACompanion,
    numberOfCompanions,
    handleAdditionalPassengerFormSubmit,
  } = usePassengerCompanionLogic({
    control,
    setValue,
    mainPassengerForm,
    passengers,
    update,
  });

  return (
    <>
      <div className="flex flex-col items-center justify-center bg-cnam-light-100 w-full h-full pb-12">
        {isLoading && <SearchLoader type={"outward"} />}
        <div className="flex flex-col gap-10 w-full">
          <div className="flex flex-col gap-4">
            <h1 className="text-cnam-blue font-jakarta text-[32px] md:text-[40px]">
              {t("booking.title")}
            </h1>
            <p className="text-base font-jakarta text-[20px]">
              {t("booking.authorizationText")}
            </p>
          </div>

          <div className=" flex flex-col gap-8">
            <div className="block-section flex flex-col gap-6">
              <h2 className="block-title">
                {t("booking.authorizationNumber")}
              </h2>
              <DAPNumber
                dap={dap}
                isLoading={isLoadingDAP}
                errorMessage={errorDapMessage}
              />
            </div>
          </div>
          {dap && canBook(dap) && !errorDapMessage && (
            <>
              <PassengerForm
                dap={dap}
                cardTypes={cardTypes}
                wheelchairTypes={wheelchairTypes}
                disabilityTypes={disabilityTypes}
                control={mainPassengerForm.control}
                ref={passengerFormRef}
                canBeACompanion={canBeACompanion("beneficiary")}
              />
              <AdditionalPassengerForm
                dap={dap}
                cardTypes={cardTypes}
                wheelchairTypes={wheelchairTypes}
                disabilityTypes={disabilityTypes}
                control={control}
                canBeACompanion={canBeACompanion}
                onAdditionalPassengerFormSubmit={
                  handleAdditionalPassengerFormSubmit
                }
                numberOfCompanions={numberOfCompanions}
                passengers={passengers}
                append={append}
                remove={remove}
                update={update}
              />
              <TravelForm dap={dap} control={control} ref={travelFormRef} />
              <div className="w-full md:self-end md:w-fit">
                <Button
                  data-testid="search-submit"
                  type="submit"
                  width="100%"
                  onClick={() => onSubmit()}
                  endEnhancer={<Icon src="arrow_right_line" color="white" />}
                >
                  {t("search.search")}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <ErrorTravelOverlappingModal
        error={error}
        bypassSearch={() => onSubmit(true)}
      />
    </>
  );
}

function ErrorTravelOverlappingModal({
  error,
  bypassSearch,
}: {
  error?: TravelOverlappingError | TravelSameMonthError;
  bypassSearch: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [prevError, setPrevError] = useState<typeof error>(undefined);

  if (error && error !== prevError) {
    setPrevError(error);
    setIsOpen(true);
  }

  if (!error || !error.agency) return null;
  const agency = error.agency;

  return (
    <Modal size="default" isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <div className="flex flex-col gap-4">
        <h2 className="font-jakarta text-lg font-bold">
          {t("search.errors.overlapping.title")}
        </h2>
        <p className="text-neutral-900 whitespace-pre-line">
          {error.$typeName === "travel.v1.TravelOverlappingError" && (
            <Trans
              t={t}
              values={{
                agency: agency.alias,
                phone: agency.treepRecapPhoneNumber,
                hours: agency.openingHours,
              }}
            >
              search.errors.overlapping.exact
            </Trans>
          )}
          {error.$typeName === "travel.v1.TravelSameMonthError" && (
            <Trans
              t={t}
              values={{
                agency: agency.alias,
                phone: agency.treepRecapPhoneNumber,
                hours: agency.openingHours,
              }}
            >
              search.errors.overlapping.month
            </Trans>
          )}
        </p>
        <div className="flex justify-end gap-4">
          {error.$typeName === "travel.v1.TravelOverlappingError" && (
            <Button
              type="button"
              size="compact"
              kind="primary"
              onClick={() => setIsOpen(false)}
            >
              {t("modal.close")}
            </Button>
          )}
          {error.$typeName === "travel.v1.TravelSameMonthError" && (
            <>
              <Button
                type="button"
                size="compact"
                kind="outline"
                onClick={() => setIsOpen(false)}
              >
                {t("modal.close")}
              </Button>
              <Button
                type="button"
                size="compact"
                kind="primary"
                onClick={() => {
                  setIsOpen(false);
                  bypassSearch();
                }}
              >
                {t("search.errors.overlapping.search")}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
